<script setup lang="ts">
import type { Tabs, TabsSize } from './types';
import { ChevronDown } from 'lucide';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useControlSize } from '@/components/base/_internal/control';
import SphIcon from '@/components/base/icon/Icon.vue';
import { useDeviceContext } from '@/composables/device-context';
import { useWindowResize } from '@/composables/window-events';
import { cn } from '@/utils/shadcn/lib/utils';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    class?: string
    buttonClass?: string
    selectClass?: string
    tabs: Tabs
    /** Matches the button heights. Falls back to the size a container provides (see `SphControlProvider`), then to `lg`. */
    size?: TabsSize
    /** `auto` collapses the tabs into a select below the `md` breakpoint, `always` regardless of the viewport. */
    collapse?: 'auto' | 'always'
}>();

const controlSize = useControlSize(() => props.size, 'lg');
// Tabs have no `sm`: a container asking for it gets the smallest tab there is.
const resolvedSize = computed<TabsSize>(() => controlSize.value === 'lg' ? 'lg' : 'md');

const sizeClasses: Record<TabsSize, { button: string, select: string }> = {
    md: { button: 'h-(--button-height-md) text-base px-2.5', select: 'h-(--button-height-md) text-base' },
    lg: { button: 'h-(--button-height-lg) text-lg px-3', select: 'h-(--button-height-lg) text-lg' },
};

const { isMobile } = useDeviceContext();
const collapsed = computed(() => props.collapse === 'always' || isMobile.value);
const activeTab = defineModel<string>();

const tabSelect = useTemplateRef('tabSelect');
const tabRefs = ref<HTMLButtonElement[]>([]);
const indicatorStyle = ref({ left: '0px', width: '0px' });

function updateIndicator() {
    if (collapsed.value) {
        indicatorStyle.value = {
            left: '0px',
            width: '100%',
        };
        return;
    }

    const idx = props.tabs.findIndex((t) => t.value === activeTab.value);
    const el = tabRefs.value[idx];
    if (el) {
        indicatorStyle.value = {
            left: `${el.offsetLeft}px`,
            width: `${el.offsetWidth}px`,
        };
    }
}

watch([() => activeTab.value, resolvedSize, collapsed], () => nextTick(updateIndicator));
onMounted(() => nextTick(updateIndicator));

useWindowResize(() => {
    nextTick(updateIndicator);
});

function focusSelect(ev: MouseEvent) {
    if ((ev?.target as HTMLElement | null)?.tagName === 'SELECT') {
        return;
    }

    tabSelect.value?.focus();
    try {
        tabSelect.value?.showPicker();
    } catch (error) {
        console.warn(error);
    }
}
</script>

<template>
    <div :class="cn('relative flex flex-row', props.class)">
        <div
            v-if="collapsed"
            :class="cn('relative flex items-center px-3', props.selectClass)"
            @click="focusSelect"
        >
            <select
                ref="tabSelect"
                v-model="activeTab"
                class="sph-form-control"
                :class="cn(
                    'flex-auto text-center border-none placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground min-w-0 appearance-none rounded-md bg-transparent px-0 py-2 leading-none font-medium text-accent-dark transition-color outline-none disabled:pointer-events-none',
                    sizeClasses[resolvedSize].select,
                    props.selectClass,
                )"
            >
                <option
                    v-for="tab in tabs"
                    :key="tab.value"
                    :value="tab.value"
                    :disabled="tab.disabled"
                    class="text-foreground"
                >
                    {{ tab.label }}
                </option>
            </select>

            <SphIcon
                :icon="ChevronDown"
                size="sm"
                class="ml-1.5 text-muted-foreground/60 pointer-events-none"
            />

            <span
                class="absolute left-0 bottom-0 w-full h-0.5 bg-accent transition-all duration-300 ease-in-out"
                :style="indicatorStyle"
            ></span>
        </div>

        <template v-else>
            <button
                v-for="(tab, i) in tabs"
                :key="tab.value"
                :ref="(el) => { tabRefs[i] = el as HTMLButtonElement }"
                :data-selected="activeTab === tab.value ? true : undefined"
                :disabled="tab.disabled"
                :class="cn(
                    'flex items-center gap-1.5 font-medium leading-none py-2 text-muted-foreground whitespace-nowrap select-none enabled:hover:text-foreground disabled:text-muted-foreground/60 disabled:cursor-not-allowed transition-colors duration-200 data-selected:text-accent-dark',
                    sizeClasses[resolvedSize].button,
                    props.buttonClass,
                )"
                @click="activeTab = tab.value"
            >
                <span class="w-full whitespace-normal text-center wrap-break-word">{{ tab.label }}</span>
            </button>

            <span
                class="absolute bottom-0 h-0.5 bg-accent transition-all duration-300 ease-in-out"
                :style="indicatorStyle"
            ></span>
        </template>
    </div>
</template>
